import React from 'react';
import { ChevronRight } from 'lucide-react';
import type {
  CampaignFeasibilityResponse,
  FlightFeasibilityResult,
  FlightDiagnostic,
} from '../../services/feasibilityService';

// Mirrors the constants in selfservice-api/src/modules/inventory/inventory.service.ts.
// Kept in sync manually — this component is dev-only, intentional duplication.
const SHARE_OF_VOICE = 0.125;
const BID_REQUEST_LOOKBACK_DAYS = 30;

interface InventoryDiagnosticProps {
  verdict: CampaignFeasibilityResponse | null;
  loading: boolean;
  error: string | null;
  budget: number | null | undefined;
  startDate: string | undefined;
  endDate: string | undefined;
  paymentPlan: 'upfront' | 'monthly' | null | undefined;
  targetingMode: 'default' | 'custom' | undefined;
  /** CTV-474: full geography selection so the diagnostic can show every list. */
  geoMode?: 'radius' | 'dmas' | 'states' | undefined;
  geoTargetDMAs?: string[] | undefined;
  geoTargetStates?: string[] | undefined;
  /** Resolved zips inside the radius circle (when radius mode is active). */
  radiusResolvedZips?: string[] | undefined;
  /** Current radius pin from the form, for clarity in the diagnostic. */
  radiusCenter?: { lat: number; lng: number; label?: string } | null | undefined;
  radiusMiles?: number | undefined;
  /**
   * Dev-only feasibility bypass. When true, the wizard's Continue gate and
   * submit-time re-check ignore an infeasible verdict so the tester can keep
   * submitting while CTV-536 is being fixed. Only ever rendered inside the
   * dev-only panel below.
   */
  bypassEnabled: boolean;
  onToggleBypass: (next: boolean) => void;
}

const fmtNum = (n: number, digits = 0) =>
  Number.isFinite(n)
    ? n.toLocaleString('en-US', { maximumFractionDigits: digits, minimumFractionDigits: digits })
    : '—';

const fmtMoney = (n: number) =>
  Number.isFinite(n)
    ? `$${n.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`
    : '—';

const fmtPct = (n: number, digits = 1) =>
  Number.isFinite(n) ? `${(n * 100).toFixed(digits)}%` : '—';

const fmtDate = (iso: string) => {
  const d = new Date(iso + 'T00:00:00Z');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

const Row: React.FC<{ label: string; value: React.ReactNode; mono?: boolean; note?: string }> = ({
  label,
  value,
  mono,
  note,
}) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5 border-b border-slate-100 last:border-b-0">
    <div className="text-[11px] text-slate-500 font-medium">
      {label}
      {note && <span className="ml-1 text-slate-400 font-normal">({note})</span>}
    </div>
    <div className={`text-[12px] text-slate-900 ${mono ? 'tabular-nums font-mono' : 'tabular-nums'}`}>
      {value}
    </div>
  </div>
);

const BookedBreakdown: React.FC<{ diag: FlightDiagnostic }> = ({ diag }) => {
  const [open, setOpen] = React.useState(false);
  const liSum = diag.lineItems.reduce((acc, li) => acc + li.contribution, 0);
  const closes = Math.abs(liSum - diag.bookedTotal) < 1;
  return (
    <div className="mt-2 pt-2 border-t border-slate-200">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left text-[10px] font-semibold uppercase text-slate-500 mb-1 hover:text-slate-700"
      >
        <span>
          How booked was calculated · {diag.lineItems.length} line item
          {diag.lineItems.length === 1 ? '' : 's'} contributing
        </span>
        <span className="text-[10px]">{open ? '▼' : '▶'}</span>
      </button>
      {open && (
        <div>
          <div className="text-[10px] text-slate-500 mb-2 leading-relaxed">
            Per-LI fanned-out load = remaining / days_left, distributed across the LI's effective
            channels weighted by bid-request share within the LI's allowed geos (ADR-0014). Contribution
            on this station = LI's daily load × this station's per-channel fan-out share over each
            overlap day. Flight booked = Σ contributions.
          </div>
          {diag.lineItems.length === 0 && (
            <div className="text-[11px] text-slate-500 italic">
              No line items targeting this station overlap the window.
            </div>
          )}
          <div className="space-y-2">
            {diag.lineItems.map((li) => (
              <div
                key={li.lineItemId}
                className="rounded border border-slate-200 bg-white px-2 py-1.5"
              >
                <div className="text-[11px] font-semibold text-slate-800 truncate">
                  #{li.lineItemId} — {li.lineItemName || '(unnamed)'}
                </div>
                <div className="text-[10px] text-slate-500 mb-1">
                  targeting: {li.sites?.length ? `sites=[${li.sites.join(',')}]` : ''}
                  {li.sites?.length && li.channelGroupIds?.length ? ' · ' : ''}
                  {li.channelGroupIds?.length
                    ? `groups=[${li.channelGroupIds.join(',')}]`
                    : ''}
                  {!li.sites?.length && !li.channelGroupIds?.length ? '(none)' : ''}
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] tabular-nums">
                  <div className="text-slate-500">quantity</div>
                  <div className="text-right">{fmtNum(li.quantity)}</div>
                  <div className="text-slate-500">delivered</div>
                  <div className="text-right">{fmtNum(li.delivered)}</div>
                  <div className="text-slate-500">remaining</div>
                  <div className="text-right">{fmtNum(li.remaining)}</div>
                  <div className="text-slate-500">days_left</div>
                  <div className="text-right">{li.daysLeft}</div>
                  <div className="text-slate-500">per_day_booked</div>
                  <div className="text-right">
                    {fmtNum(li.perDayBooked, 2)}
                    <span className="text-slate-400 text-[10px]">
                      {' '}= {fmtNum(li.remaining)} / {li.daysLeft}
                    </span>
                  </div>
                  <div className="text-slate-500">overlap_days</div>
                  <div className="text-right">{li.overlapDays}</div>
                  {/* CTV-536: only the station-wide path computes a per-LI
                      channel share. The geo path leaves it null. */}
                  {li.channelShare != null && (
                    <>
                      <div className="text-slate-500">fan-out share</div>
                      <div className="text-right">
                        {fmtPct(li.channelShare, 1)}
                        <span className="text-slate-400 text-[10px]">
                          {' '}of LI's fanned-out load
                        </span>
                      </div>
                    </>
                  )}
                  <div className="text-slate-700 font-semibold">contribution</div>
                  <div className="text-right font-semibold">
                    {fmtNum(li.contribution)}
                    {li.channelShare != null ? (
                      <span className="text-slate-400 text-[10px] font-normal">
                        {' '}= LI's daily booked × {fmtPct(li.channelShare, 1)} (per-channel)
                      </span>
                    ) : (
                      <span className="text-slate-400 text-[10px] font-normal">
                        {' '}= {fmtNum(li.perDayBooked, 2)} × {li.overlapDays}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div
            className={`mt-2 px-2 py-1.5 rounded text-[11px] tabular-nums ${
              closes
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
          >
            Σ contributions = <strong>{fmtNum(liSum)}</strong>; flight booked ={' '}
            <strong>{fmtNum(diag.bookedTotal)}</strong>
            {closes ? ' ✓ matches' : ' ✗ mismatch — math does not close'}
          </div>
        </div>
      )}
    </div>
  );
};

const FlightCard: React.FC<{
  flight: FlightFeasibilityResult;
  index: number;
  cpm: number;
  diagnostic?: FlightDiagnostic;
}> = ({
  flight,
  index,
  cpm,
  diagnostic,
}) => {
  const tone = !flight.feasible ? 'border-red-300 bg-red-50/40' : 'border-slate-200 bg-white';
  const computedRequested = cpm > 0 ? (flight.flightBudget / cpm) * 1000 : 0;
  const computedBidRequests = flight.dailyBidRequestsAverage * flight.daysInFlight;
  const computedSov = flight.availableImpressions * SHARE_OF_VOICE;
  const computedHeadroom = flight.availableImpressions - flight.bookedImpressions;
  const computedNet = Math.max(0, Math.min(computedSov, computedHeadroom));

  return (
    <div className={`rounded-lg border ${tone} p-3`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-700">
            Flight #{index + 1}
            {flight.isPartial && (
              <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-bold">
                PARTIAL
              </span>
            )}
            {flight.installmentNumber !== null && (
              <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[9px] font-bold">
                INSTALLMENT {flight.installmentNumber}
              </span>
            )}
          </span>
          <div className="text-[12px] text-slate-700 mt-0.5">
            {fmtDate(flight.startDate)} → {fmtDate(flight.endDate)}
          </div>
        </div>
        <div
          className={`text-[11px] font-bold px-2 py-1 rounded-full ${
            flight.feasible ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {flight.feasible ? 'FEASIBLE' : 'INFEASIBLE'}
        </div>
      </div>

      <Row
        label="Period days"
        value={
          <span>
            <strong>{flight.daysInFlight}</strong> actual
            {flight.naturalDaysInFlight !== flight.daysInFlight && (
              <span className="text-slate-500">
                {' '}/ {flight.naturalDaysInFlight} natural{' '}
                <em className="text-amber-700">(clipped by campaign end)</em>
              </span>
            )}
          </span>
        }
      />
      <Row label="Flight budget" value={fmtMoney(flight.flightBudget)} note="budget × days/total" />
      <Row
        label="Requested impressions"
        value={
          <span>
            <strong>{fmtNum(flight.requestedImpressions)}</strong>{' '}
            <span className="text-slate-400 text-[10px]">
              = ({fmtMoney(flight.flightBudget)} / {fmtMoney(cpm)}) × 1000 ≈ {fmtNum(computedRequested, 2)}
            </span>
          </span>
        }
      />

      <div className="mt-2 pt-2 border-t border-slate-200">
        <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Supply side</div>
        <Row
          label="Daily bid requests"
          value={fmtNum(flight.dailyBidRequestsAverage)}
          note={`${BID_REQUEST_LOOKBACK_DAYS}-day avg`}
        />
        <Row
          label="Bid requests in flight"
          value={
            <span>
              {fmtNum(computedBidRequests)}{' '}
              <span className="text-slate-400 text-[10px]">
                = {fmtNum(flight.dailyBidRequestsAverage)} × {flight.daysInFlight}
              </span>
            </span>
          }
        />
        <Row
          label="Available impressions"
          value={
            <span>
              <strong>{fmtNum(flight.availableImpressions)}</strong>{' '}
              <span className="text-slate-400 text-[10px]">
                = bid_requests × render_rate
              </span>
            </span>
          }
        />
        <Row
          label="Booked impressions"
          value={fmtNum(flight.bookedImpressions)}
          note="existing line items in period"
        />
        <Row
          label="Utilization"
          value={
            <span>
              {fmtPct(flight.utilization)}{' '}
              <span className="text-slate-400 text-[10px]">= booked / available</span>
            </span>
          }
        />
        <Row
          label="SOV-capped"
          value={
            <span>
              {fmtNum(flight.sovCappedImpressions)}{' '}
              <span className="text-slate-400 text-[10px]">
                = available × {SHARE_OF_VOICE} ({fmtPct(SHARE_OF_VOICE, 1)} SOV) ≈ {fmtNum(computedSov)}
              </span>
            </span>
          }
        />
        <Row
          label="Available headroom"
          value={
            <span className={computedHeadroom <= 0 ? 'text-red-700 font-semibold' : ''}>
              {fmtNum(computedHeadroom)}{' '}
              <span className="text-slate-400 text-[10px]">= available − booked</span>
            </span>
          }
        />
        <Row
          label="Net available"
          value={
            <span className="font-semibold">
              {fmtNum(flight.netAvailableImpressions)}{' '}
              <span className="text-slate-400 text-[10px] font-normal">
                = max(0, min(sov_cap, headroom)) ≈ {fmtNum(computedNet)}
              </span>
            </span>
          }
        />
      </div>

      {/* CTV-474: when a geo selection is in play, surface both the
          station-wide and geo-filtered numbers so it's clear which side
          bound the verdict. The "Supply side" rows above show the
          *effective* numbers (whichever wins min()); these rows show the
          full breakdown. */}
      {flight.geoAvailableImpressions != null && (
        <div className="mt-2 pt-2 border-t border-slate-200">
          <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">
            Station vs geo breakdown
          </div>
          <Row
            label="Daily bid requests"
            value={
              <span>
                station <strong>{fmtNum(flight.stationDailyBidRequestsAverage)}</strong>{' '}
                · geo <strong>{fmtNum(flight.geoDailyBidRequestsAverage ?? 0)}</strong>
                <span className="text-slate-400 text-[10px]">
                  {' '}({fmtPct(
                    flight.stationDailyBidRequestsAverage > 0
                      ? (flight.geoDailyBidRequestsAverage ?? 0) /
                          flight.stationDailyBidRequestsAverage
                      : 0,
                  )} of station)
                </span>
              </span>
            }
            note="30-day avg"
          />
          <Row
            label="Available impressions"
            value={
              <span>
                station <strong>{fmtNum(flight.stationAvailableImpressions)}</strong>{' '}
                · geo <strong>{fmtNum(flight.geoAvailableImpressions ?? 0)}</strong>
              </span>
            }
          />
          <Row
            label="Booked impressions"
            value={
              <span>
                station <strong>{fmtNum(flight.stationBookedImpressions)}</strong>{' '}
                · geo <strong>{fmtNum(flight.geoBookedImpressions ?? 0)}</strong>
              </span>
            }
            note="competing line items"
          />
          <Row
            label="SOV cap"
            value={
              <span>
                station <strong>{fmtNum(flight.stationSovCappedImpressions)}</strong>{' '}
                · geo <strong>{fmtNum(flight.geoSovCappedImpressions ?? 0)}</strong>
              </span>
            }
            note={`available × ${SHARE_OF_VOICE}`}
          />
          <Row
            label="Net available"
            value={
              <span>
                station <strong>{fmtNum(flight.stationNetAvailableImpressions)}</strong>{' '}
                · geo <strong>{fmtNum(flight.geoNetAvailableImpressions ?? 0)}</strong>{' '}
                <span className="text-slate-400 text-[10px]">
                  effective = min({fmtNum(flight.stationNetAvailableImpressions)}, {fmtNum(flight.geoNetAvailableImpressions ?? 0)}) = {fmtNum(flight.netAvailableImpressions)}
                </span>
              </span>
            }
          />
        </div>
      )}

      <div className="mt-2 pt-2 border-t border-slate-200">
        <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Verdict</div>
        <Row
          label="Feasibility check"
          value={
            <span className={flight.feasible ? 'text-emerald-700' : 'text-red-700 font-semibold'}>
              {fmtNum(flight.requestedImpressions)} requested {flight.feasible ? '≤' : '>'}{' '}
              {fmtNum(flight.netAvailableImpressions)} net available
            </span>
          }
        />
        <Row
          label="Constraint"
          value={
            <span className={flight.constraintKind === 'station' ? '' : 'text-amber-700 font-semibold'}>
              {flight.constraintKind === 'geo'
                ? 'selected geo bound first'
                : flight.constraintKind === 'both'
                  ? 'both geo and station bound simultaneously'
                  : 'station-wide'}
            </span>
          }
        />
      </div>

      {diagnostic && <BookedBreakdown diag={diagnostic} />}
    </div>
  );
};

const InventoryDiagnostic: React.FC<InventoryDiagnosticProps> = ({
  verdict,
  loading,
  error,
  budget,
  startDate,
  endDate,
  paymentPlan,
  targetingMode,
  geoMode,
  geoTargetDMAs,
  geoTargetStates,
  radiusResolvedZips,
  radiusCenter,
  radiusMiles,
  bypassEnabled,
  onToggleBypass,
}) => {
  const [open, setOpen] = React.useState(false);

  // Gate: dev builds only. Bundlers strip this branch in production.
  if (!import.meta.env.DEV) return null;

  const summary = loading
    ? 'loading…'
    : error && !verdict
      ? `error: ${error}`
      : verdict
        ? `${verdict.feasible ? 'feasible' : 'infeasible'} · ${fmtNum(verdict.requestedImpressionsTotal)} requested / ${fmtNum(verdict.netAvailableImpressionsTotal)} net available`
        : 'no verdict yet';

  const showBypassOffer = !bypassEnabled && !!verdict && !verdict.feasible;

  return (
    <div className="mt-8 rounded-2xl border-2 border-dashed border-fuchsia-300 bg-fuchsia-50/30 p-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex-1 min-w-0 flex items-center gap-2 text-left hover:opacity-80 transition-opacity"
          aria-expanded={open}
        >
          <ChevronRight
            className={`w-4 h-4 text-slate-500 transition-transform ${open ? 'rotate-90' : ''}`}
          />
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded bg-fuchsia-200 text-fuchsia-900">
            DEV ONLY
          </span>
          <h3 className="text-[14px] font-semibold tracking-tight text-slate-900">
            Inventory feasibility diagnostic
          </h3>
          <span className="ml-auto text-[11px] text-slate-500 truncate">{summary}</span>
        </button>
        {bypassEnabled && (
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded bg-amber-200 text-amber-900">
              Bypass active
            </span>
            <button
              type="button"
              onClick={() => onToggleBypass(false)}
              className="text-[11px] text-slate-600 hover:text-slate-900 underline underline-offset-2"
            >
              Restore check
            </button>
          </div>
        )}
        {showBypassOffer && (
          <button
            type="button"
            onClick={() => onToggleBypass(true)}
            className="shrink-0 text-[11px] font-semibold tracking-tight px-3 py-1 rounded-full bg-fuchsia-600 text-white hover:bg-fuchsia-700 transition-colors"
          >
            Bypass check &amp; continue (dev)
          </button>
        )}
      </div>

      <div
        className="grid transition-[grid-template-rows] duration-300 ease-out"
        style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
        aria-hidden={!open}
      >
        <div className="overflow-hidden">
          <div className="pt-4">
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Inputs</div>
          <Row label="Budget" value={budget ? fmtMoney(budget) : '—'} />
          <Row label="Start date" value={startDate || '—'} />
          <Row label="End date" value={endDate || '—'} />
          <Row label="Payment plan" value={paymentPlan || '—'} />
          <Row label="Targeting mode" value={targetingMode || '—'} />
          <Row label="Geo mode" value={geoMode ?? '—'} />
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Backend state</div>
          {loading && <div className="text-[12px] text-slate-500">Loading…</div>}
          {error && !verdict && <div className="text-[12px] text-red-700">Error: {error}</div>}
          {verdict && (
            <>
              <Row label="Station" value={verdict.stationName} />
              <Row label="Product" value={verdict.productName} />
              <Row label="CPM" value={fmtMoney(verdict.cpm)} />
              <Row
                label="Render rate"
                value={
                  <span>
                    {fmtPct(verdict.renderRate, 2)}{' '}
                    {verdict.isDefaultRenderRate && (
                      <span className="text-amber-700 text-[10px]">(org-wide fallback)</span>
                    )}
                  </span>
                }
              />
              <Row label="SOV constant" value={fmtPct(SHARE_OF_VOICE, 1)} />
              <Row
                label="Unmatched line items"
                value={
                  <span className={verdict.unmatchedLineItemCount > 0 ? 'text-amber-700 font-semibold' : ''}>
                    {verdict.unmatchedLineItemCount}{' '}
                    {verdict.unmatchedLineItemCount > 0 && (
                      <span className="text-[10px] font-normal">(data-sync gap)</span>
                    )}
                  </span>
                }
              />
            </>
          )}
        </div>
      </div>

      {/* CTV-474: explicit "what the buyer picked" panel — kept outside the
          `verdict && …` gate so it renders even before the first response. */}
      {targetingMode === 'custom' && (
        <div className="rounded-lg border border-slate-200 bg-white p-3 mb-4">
          <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">
            Geography selection
          </div>
          <Row label="Mode" value={geoMode ?? '—'} />
          {geoMode === 'radius' && (
            <>
              <Row
                label="Center"
                value={
                  radiusCenter
                    ? `${radiusCenter.label ?? 'pin'} @ (${radiusCenter.lat.toFixed(4)}, ${radiusCenter.lng.toFixed(4)})`
                    : '—'
                }
              />
              <Row label="Radius" value={radiusMiles != null ? `${radiusMiles} mi` : '—'} />
              <Row
                label="Resolved ZIPs"
                value={
                  radiusResolvedZips?.length
                    ? `${radiusResolvedZips.length} (${radiusResolvedZips.slice(0, 5).join(', ')}${radiusResolvedZips.length > 5 ? '…' : ''})`
                    : '—'
                }
              />
            </>
          )}
          {geoMode === 'dmas' && (
            <Row
              label="Selected metros"
              value={
                geoTargetDMAs?.length
                  ? `${geoTargetDMAs.length} (${geoTargetDMAs.slice(0, 10).join(', ')}${geoTargetDMAs.length > 10 ? '…' : ''})`
                  : '—'
              }
            />
          )}
          {geoMode === 'states' && (
            <Row
              label="Selected states"
              value={
                geoTargetStates?.length
                  ? `${geoTargetStates.length} (${geoTargetStates.slice(0, 15).join(', ')})`
                  : '—'
              }
            />
          )}
          {verdict?.geoCoverage && (
            <>
              <Row
                label="Coverage"
                value={`${verdict.geoCoverage.percent}% of ${verdict.stationName} bid-request volume`}
              />
              <Row
                label="Coverage kind"
                value={verdict.geoCoverage.kind}
              />
            </>
          )}
          {verdict?.geoRecommendations && (
            <Row
              label="Top unselected geos"
              value={`${verdict.geoRecommendations.codes.slice(0, 3).join(', ')} (${verdict.geoRecommendations.kind})`}
            />
          )}
          {verdict?.stationCenter && (
            <Row
              label="Station home center"
              value={`${verdict.stationCenter.label} (${verdict.stationCenter.lat.toFixed(3)}, ${verdict.stationCenter.lon.toFixed(3)})`}
            />
          )}
        </div>
      )}

      {verdict && (
        <>
          <div className="rounded-lg border border-slate-200 bg-white p-3 mb-4">
            <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">
              Top-line verdict
            </div>
            <Row
              label="Overall feasible"
              value={
                <span className={verdict.feasible ? 'text-emerald-700 font-semibold' : 'text-red-700 font-semibold'}>
                  {verdict.feasible ? 'YES' : 'NO'}
                </span>
              }
            />
            <Row label="Total requested" value={fmtNum(verdict.requestedImpressionsTotal)} />
            <Row label="Total net available" value={fmtNum(verdict.netAvailableImpressionsTotal)} />
            <Row
              label="Failing flights"
              value={
                verdict.failingFlightIndexes.length === 0
                  ? 'none'
                  : verdict.failingFlightIndexes.map((i) => `#${i + 1}`).join(', ')
              }
            />
            <Row
              label="Extension date"
              value={verdict.extensionDate ?? 'n/a (feasible or > 12-month cap)'}
            />
            {verdict.geoCoverage && (
              <Row
                label="Geo coverage"
                value={`${verdict.geoCoverage.percent}% (${verdict.geoCoverage.kind})`}
              />
            )}
            {verdict.geoRecommendations && (
              <Row
                label="Geo recommendations"
                value={`${verdict.geoRecommendations.codes.slice(0, 3).join(', ')} (${verdict.geoRecommendations.kind})`}
              />
            )}
            {verdict.stationCenter && (
              <Row
                label="Station center"
                value={`${verdict.stationCenter.label} (${verdict.stationCenter.lat.toFixed(3)}, ${verdict.stationCenter.lon.toFixed(3)})`}
              />
            )}
            {verdict.diagnostic?.extensionSearch && (
              <Row
                label="Extension search"
                value={`${verdict.diagnostic.extensionSearch.candidates.length} candidates${verdict.diagnostic.extensionSearch.cappedAt ? ` (capped at ${verdict.diagnostic.extensionSearch.cappedAt})` : ''}`}
              />
            )}
          </div>

          <div className="space-y-2">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-700">
              Per-flight breakdown ({verdict.flightBreakdown.length} flights)
            </div>
            {verdict.flightBreakdown.map((flight, i) => (
              <FlightCard
                key={i}
                flight={flight}
                index={i}
                cpm={verdict.cpm}
                diagnostic={verdict.diagnostic?.perFlight.find((f) => f.flightIndex === i)}
              />
            ))}
          </div>
        </>
      )}

      <div className="mt-3 text-[10px] text-slate-500 leading-relaxed">
        <strong>Calculation flow:</strong> daily_bid_requests (30-day avg) × period_days → bid_requests_in_flight × render_rate → available_impressions.{' '}
        sov_cap = available × {SHARE_OF_VOICE}. headroom = available − booked. net_available = max(0, min(sov_cap, headroom)).{' '}
        requested = (flight_budget / cpm) × 1000. <strong>feasible</strong> = requested ≤ net_available. <strong>constraintKind</strong>: 'geo' when the geo-filtered cap binds, 'station' when station-wide binds, 'both' on simultaneous failure.
      </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryDiagnostic;
